// Billing — a home's settled months from Divako Settlement: the web twin of the
// mobile Billing tab, laid out like Home. The topbar picker chooses the home;
// the toolbar steps through the settled months and opens the month's bill as a
// PDF. The month's total and how it divides sit on top, its bills below as
// tiles, and the selected bill's detail renders inline under them (BillPanel)
// — the legacy portal's select-a-card pattern instead of the mobile app's
// drill-down screens. Besides the months, the picker offers the year to date
// (lib/billingYear.ts), after the customer's mockup. Home, period and bill ride
// in the URL (?g=&month=&b=) so the view is bookmarkable. Only for the owner of a home whose project shows
// Billing (billingHomes); the topbar offers the page to nobody else.

import type { CSSProperties, ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motionSafeBehavior } from '../lib/a11y';
import {
  ApiError,
  getMyBilling,
  myBillPdfUrl,
  type Billing,
  type BillingSettlement,
} from '../lib/api';
import {
  billingHomes,
  categoryRows,
  dayRange,
  formatDay,
  formatMoney,
  formatQuantity,
  monthChip,
  monthTitle,
  monthTotals,
  previousSettlement,
  type CategoryRow,
} from '../lib/billing';
import { latestYear, yearToDate } from '../lib/billingYear';
import { rgbString } from '../lib/colors';
import { useEdgeFade } from '../lib/edgeFade';
import { groupKey, useGroupName, useSelectedGroup } from '../lib/group';
import { useUser } from '../lib/user';
import BillPanel from '../components/BillPanel';
import { billKindStyle, ChangeText, KindBubble, rowKind } from '../components/BillingParts';
import Icon from '../components/Icon';
import { Dropdown, Spinner, StateCard } from '../components/ui';

// The home's settled months. Under `npm run dev`, while portal-api or
// Settlement cannot answer yet (404 from a portal-api without the route,
// 502/503 from one without Settlement), sample bills stand in so the page can
// be built — `sample` says so, and the page shows it. A production build drops
// the branch and the sample module with it.
async function loadBilling(gid: string): Promise<{ billing: Billing; sample: boolean }> {
  try {
    return { billing: await getMyBilling(gid), sample: false };
  } catch (e) {
    if (import.meta.env.DEV && e instanceof ApiError && [404, 502, 503].includes(e.status)) {
      const { sampleBilling } = await import('../lib/billingSample');
      return { billing: sampleBilling(), sample: true };
    }
    throw e;
  }
}

export default function BillingPage(): ReactElement {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { me, loading: meLoading, error: meError, reload } = useUser();
  const { group } = useSelectedGroup();
  const groupName = useGroupName();
  const [params, setParams] = useSearchParams();

  const homes = useMemo(() => billingHomes(me?.categories_and_groups ?? []), [me]);
  // The picked entry when it is a home with bills, else the first one; the
  // URL follows, so the topbar picker names the home on screen.
  const home = group && homes.includes(group) ? group : (homes[0] ?? null);
  const gid = home?.id ?? null;

  useEffect(() => {
    if (home && home !== group) setParams({ g: groupKey(home) }, { replace: true });
  }, [home, group, setParams]);

  const [billing, setBilling] = useState<Billing | null>(null);
  const [sample, setSample] = useState(false);
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0); // retry trigger

  // Always fresh when the page opens a home; its months all come at once.
  useEffect(() => {
    if (!gid) return;
    let alive = true;
    setBilling(null);
    setFailed(false);
    loadBilling(gid)
      .then((res) => {
        if (!alive) return;
        setBilling(res.billing);
        setSample(res.sample);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [gid, nonce]);

  // Newest first. ?month is a month's label or the year to date's ("ytd-2026");
  // anything else opens on the newest month. An unknown or missing ?b opens on
  // the largest bill. The stepper walks the months only.
  const settlements = useMemo(() => billing?.settlements ?? [], [billing]);
  const year = latestYear(settlements);
  const ytd = useMemo(() => (year != null ? yearToDate(settlements, year) : null), [settlements, year]);
  const wanted = params.get('month');
  const isYear = ytd != null && wanted === ytd.label;
  const index = Math.max(0, settlements.findIndex((s) => s.label === wanted));
  const current: BillingSettlement | null = isYear ? ytd : (settlements[index] ?? null);
  const older = isYear ? null : (settlements[index + 1] ?? null);
  const newer = !isYear && index > 0 ? settlements[index - 1] : null;
  const prev = current && !isYear ? previousSettlement(settlements, current.label) : undefined;
  const rows = useMemo(() => (current ? categoryRows(current, prev) : []), [current, prev]);
  const selected = rows.find((r) => r.code === params.get('b')) ?? rows[0] ?? null;
  const category = selected ? (current?.categories.find((c) => c.code === selected.code) ?? null) : null;

  const panelRef = useRef<HTMLDivElement>(null);
  const gridRef = useEdgeFade<HTMLDivElement>();
  const scrollOnSelect = useRef(false);

  const pickMonth = (label: string) => {
    if (home) setParams({ g: groupKey(home), month: label }, { replace: true });
  };

  const pickBill = (code: string) => {
    if (!home || !current) return;
    scrollOnSelect.current = true;
    setParams({ g: groupKey(home), month: current.label, b: code }, { replace: true });
  };

  // Bring the panel into view on a tile click (not when the page opens).
  useEffect(() => {
    if (!scrollOnSelect.current || !selected) return;
    scrollOnSelect.current = false;
    panelRef.current?.scrollIntoView({ behavior: motionSafeBehavior(), block: 'start' });
  }, [selected]);

  if (meLoading) return <Spinner />;
  if (meError) {
    return (
      <StateCard
        title={t('common.error')}
        body={meError}
        action={
          <button className="btn" onClick={reload}>
            <Icon name="refresh" size={16} /> {t('common.retry')}
          </button>
        }
      />
    );
  }
  if (!home) return <Navigate to="/" replace />;

  const title = (s: BillingSettlement) =>
    s === ytd
      ? t('billingPage.yearToDate', { year })
      : s.pre_settlement
        ? t('billing.preSettlement')
        : monthTitle(s.label, lang);
  const unitName =
    billing?.unit?.name && billing.unit.name !== groupName(home) ? billing.unit.name : null;
  const area = billing?.unit?.area_m2 ? t('billing.area', { area: formatQuantity(billing.unit.area_m2) }) : null;

  let body: ReactElement;
  if (failed) {
    body = (
      <StateCard
        title={t('billing.loadFailed')}
        action={
          <button className="btn" onClick={() => setNonce((n) => n + 1)}>
            <Icon name="refresh" size={16} /> {t('common.retry')}
          </button>
        }
      />
    );
  } else if (!billing) {
    body = <Spinner />;
  } else if (!billing.connected) {
    body = (
      <StateCard icon="receipt" title={t('billing.notConnectedTitle')} body={t('billing.notConnectedBody')} />
    );
  } else if (!current) {
    body = <StateCard icon="receipt" title={t('billing.noSettledTitle')} body={t('billing.noSettledBody')} />;
  } else {
    body = (
      <>
        <div className="home-toolbar">
          <div className="period-nav">
            <button
              className="icon-btn"
              disabled={!older}
              onClick={() => older && pickMonth(older.label)}
              data-tip={t('ui.previousPeriod')}
              aria-label={t('ui.previousPeriod')}
            >
              <Icon name="chevron-left" size={20} />
            </button>
            <Dropdown
              trigger={(open) => (
                <button className="period-title" aria-expanded={open}>
                  {title(current)}
                  <Icon name="chevron-down" size={16} />
                </button>
              )}
            >
              {[...settlements, ...(ytd ? [ytd] : [])].map((s) => (
                <button
                  key={s.label}
                  className={`menu-item${s === current ? ' selected' : ''}`}
                  onClick={() => pickMonth(s.label)}
                >
                  <div className="menu-item-body">
                    <div className="menu-item-title">{title(s)}</div>
                    {s.money && (
                      <div className="menu-item-sub">{formatMoney(monthTotals(s)?.total, s.currency)}</div>
                    )}
                  </div>
                  {s === current && <Icon name="check" size={16} />}
                </button>
              ))}
            </Dropdown>
            <button
              className="icon-btn"
              disabled={!newer}
              onClick={() => newer && pickMonth(newer.label)}
              data-tip={t('ui.nextPeriod')}
              aria-label={t('ui.nextPeriod')}
            >
              <Icon name="chevron-right" size={20} />
            </button>
          </div>
          <div className="spacer" />
          {/* A month's bill is a PDF of its own; the year to date has none. */}
          {!isYear &&
            (sample ? (
              <button className="icon-btn" disabled aria-label={t('billing.downloadPdf')}>
                <Icon name="download" size={20} />
              </button>
            ) : (
              <a
                className="icon-btn"
                href={myBillPdfUrl(home.id, current.label)}
                target="_blank"
                rel="noreferrer"
                data-tip={t('billing.downloadPdf')}
                aria-label={t('billing.downloadPdf')}
              >
                <Icon name="download" size={20} />
              </a>
            ))}
        </div>

        <div className="bill-summary">
          <TotalCard s={current} title={title(current)} isYear={isYear} />
          {current.money && rows.some((r) => (r.amount ?? 0) > 0) && (
            <BreakdownCard
              s={current}
              rows={rows}
              month={isYear ? title(current) : monthChip(current.label, lang).month}
            />
          )}
        </div>

        <div className="bill-strip-head">
          <h2>{t('billing.costByCategory')}</h2>
          {prev && <span>{t('billing.changeVs', { month: monthChip(prev.label, lang).month })}</span>}
        </div>
        <div ref={gridRef} className="meter-grid has-selection">
          {rows.map((r) => (
            <BillTile
              key={r.code}
              r={r}
              s={current}
              selected={r === selected}
              onSelect={() => pickBill(r.code)}
            />
          ))}
        </div>

        {selected && category && (
          <div ref={panelRef} className="inline-panel">
            <BillPanel
              key={`${current.label}-${category.code}`}
              s={current}
              c={category}
              title={title(current)}
              kind={rowKind(selected, current)}
              areaM2={billing.unit?.area_m2}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div className="titles">
          <h1>{t('nav.billing')}</h1>
          <div className="subtitle">{[home.project_name, unitName, area].filter(Boolean).join(' · ')}</div>
        </div>
      </div>
      {sample && <p className="bill-sample">{t('billing.sample')}</p>}
      {body}
    </>
  );
}

// What the period came to: the total, split into use, fixed costs and the
// corrections it carried for earlier months. `title` names the period.
function TotalCard({ s, title, isYear }: { s: BillingSettlement; title: string; isYear: boolean }): ReactElement {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const totals = monthTotals(s);
  const partial = s.from !== s.period_from || s.to !== s.period_to;
  const notes = [
    s.vat != null ? t('billing.inclVat', { amount: formatMoney(s.vat, s.currency) }) : null,
    s.settled_on && !isYear ? t('billing.settledOn', { date: formatDay(s.settled_on) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <section className="card">
      <div className="bill-label">
        {s.pre_settlement ? title : t('billing.totalFor', { month: title })}
        {' · '}
        {dayRange(s.period_from, s.period_to, lang)}
      </div>
      {totals ? (
        <div className="bill-big">{formatMoney(totals.total, s.currency)}</div>
      ) : (
        <p className="bill-note">{t('billing.usageOnly')}</p>
      )}
      {partial && <p className="bill-note">{t('billing.yourDays', { range: dayRange(s.from, s.to, lang) })}</p>}
      {totals && (
        <div className="stat-row bill-stats">
          <Stat label={t('billing.usage')} value={formatMoney(totals.usage, s.currency)} />
          <Stat label={t('billing.fixedCosts')} value={formatMoney(totals.fixed, s.currency)} />
          {totals.corrections !== 0 && (
            <Stat label={t('billing.corrections')} value={formatMoney(totals.corrections, s.currency)} />
          )}
        </div>
      )}
      {notes && <p className="bill-note muted">{notes}</p>}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

// How the period's money divides between its bills: one bar, one legend.
// `month` names the period briefly ("Aug", or the year to date).
function BreakdownCard({ s, rows, month }: { s: BillingSettlement; rows: CategoryRow[]; month: string }): ReactElement {
  const { t } = useTranslation();
  const parts = rows.filter((r) => (r.amount ?? 0) > 0 && r.share != null);
  return (
    <section className="card">
      <div className="bill-card-head">
        <div className="card-title">{t('billing.breakdown', { month })}</div>
        <span className="bill-note">
          {t('billing.ofTotal', { amount: formatMoney(monthTotals(s)?.total, s.currency) })}
        </span>
      </div>
      <div className="bill-bar" aria-hidden="true">
        {parts.map((r) => (
          <span key={r.code} style={{ flex: r.share as number, background: billKindStyle(rowKind(r, s)).color }} />
        ))}
      </div>
      <ul className="bill-legend">
        {parts.map((r) => (
          <li key={r.code}>
            <span className="bill-dot" style={{ background: billKindStyle(rowKind(r, s)).color }} />
            <span className="bill-legend-name">{r.name}</span>
            <span className="bill-row-value">{`${((r.share as number) * 100).toFixed(1)}%`}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// One bill of the month as a tile on the strip, shaped like a meter tile: what
// it came to (or the use, where the association settles use only), what it
// measured, and its change against the month before.
function BillTile({
  r,
  s,
  selected,
  onSelect,
}: {
  r: CategoryRow;
  s: BillingSettlement;
  selected: boolean;
  onSelect: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const kind = rowKind(r, s);
  const detail = r.usage
    ? formatQuantity(r.usage.quantity, r.usage.unit)
    : r.kind === 'fee'
      ? t('billing.fixedCharge')
      : t('billing.yourShare');
  return (
    <button
      onClick={onSelect}
      className={`metric-card bill-tile${selected ? ' selected' : ''}`}
      aria-pressed={selected}
      style={{ '--brand-rgb': rgbString(billKindStyle(kind).color) } as CSSProperties}
    >
      <div className="metric-head">
        <span className="metric-name">{r.name}</span>
        <KindBubble kind={kind} />
      </div>
      <div className="metric-value">
        {s.money ? (
          <>
            {/* The amount without its currency, which follows smaller. */}
            {formatMoney(r.amount, '').trimEnd()}
            {r.amount != null && <span className="metric-unit">{s.currency}</span>}
          </>
        ) : r.usage ? (
          <>
            {formatQuantity(r.usage.quantity)}
            <span className="metric-unit">{r.usage.unit}</span>
          </>
        ) : (
          '—'
        )}
      </div>
      <div className="metric-sub bill-tile-sub">
        <span>{s.money || !r.usage ? detail : ''}</span>
        <ChangeText ratio={r.change} />
      </div>
    </button>
  );
}
